import axios from 'axios';
import { globalConfig } from '../configuration/config';
import {
  TRANSFORMATION_PROFILES,
  buildTransformationSettings,
  createTransformationFormDefaults,
} from './converter-transformation-settings';

export const CONVERTER_POLL_INTERVAL_MS = 2000;
export const CONVERTER_STATUS_DELAY_WARNING_MS = 10 * 60 * 1000;
export const GLB_CONVERTER_SOURCE_FILE_NAME = 'source.glb';
export const GLB_CONVERTER_MODEL_TRANSFORM_REPORT_FILE_NAME = 'model-transform-report.json';
export const GLB_CONVERTER_SOURCE_CONTENT_TYPE = 'model/gltf-binary';
export const GLB_SOURCE_LINK_MESSAGE_PREFIX = 'Исходный GLB-файл:';
export const GLB_SOURCE_LINK_FALLBACK_MESSAGE_CONTENT = 'Не удалось прикрепить файл';
export const GLB_RETRY_ACTION_MESSAGE_CONTENT = 'Перезапустить GLB-конвертацию';
export const GLB_RETRY_ACTION_TYPE = 2;
export const GLB_CONVERTER_MAX_UPLOAD_BYTES = 1_073_741_824;
export const GLB_CONVERTER_MAX_UPLOAD_SIZE_MB = GLB_CONVERTER_MAX_UPLOAD_BYTES / (1024 * 1024);
export const GLB_CONVERTER_SCALE_MODES = [
  'auto',
  'coefficient',
  'bounds',
];
export const DEFAULT_GLB_CONVERTER_SCALE_MODE = GLB_CONVERTER_SCALE_MODES[0];
export const DEFAULT_GLB_CONVERTER_MODEL_SCALE = 'auto';
export const DEFAULT_GLB_CONVERTER_COEFFICIENT_SCALE = '1';
export const CUSTOM_GLB_CONVERTER_MODEL_SCALE = 'custom';
export const GLB_CONVERTER_MODEL_SCALE_PRESETS = [
  '1',
  '0.1',
  '0.01',
  '0.001',
];
export const GLB_CONVERTER_RENDER_OUTPUT_MODES = [
  'sequence',
  'sphere',
];
export const DEFAULT_GLB_CONVERTER_RENDER_OUTPUT_MODE =
  GLB_CONVERTER_RENDER_OUTPUT_MODES[0];
export const GLB_PENDING_RUN_PHASES = Object.freeze({
  LEGACY_CLAIMED: 'legacy_claimed',
  POLLING: 'polling',
  POLL_SUSPENDED: 'poll_suspended',
  PUBLISH_UNKNOWN: 'publish_unknown',
});
export const GLB_PENDING_RUN_STORAGE_PREFIX = '2dchat.glb-converter.pending.v1';
const DEFAULT_GLB_CONVERTER_CAPACITY_RETRY_AFTER_SECONDS = 30;
const DEFAULT_GLB_TRANSFORMATION_SETTINGS = buildTransformationSettings({
  profile: TRANSFORMATION_PROFILES.GLB_IMPORT,
  values: createTransformationFormDefaults(
    TRANSFORMATION_PROFILES.GLB_IMPORT,
  ),
});

export class ImportModelsConverterError extends Error {
  constructor(messageKey, details = {}) {
    super(messageKey);
    this.name = 'ImportModelsConverterError';
    this.messageKey = messageKey;
    this.details = details;
  }
}

function getNormalizedFileExtension(fileName) {
  if (typeof fileName !== 'string') {
    return '';
  }

  const extensionIndex = fileName.lastIndexOf('.');

  if (extensionIndex === -1) {
    return '';
  }

  return fileName.slice(extensionIndex).toLowerCase();
}

function getGlbUploadLimitErrorDetails(file) {
  const fileSize = typeof file?.size === 'number' && Number.isFinite(file.size)
    ? file.size
    : 0;

  if (fileSize <= GLB_CONVERTER_MAX_UPLOAD_BYTES) {
    return undefined;
  }

  return {
    maxSizeMb: GLB_CONVERTER_MAX_UPLOAD_SIZE_MB,
  };
}

function trimTrailingSlash(url) {
  return url.replace(/\/+$/, '');
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }

      resolve();
    }, ms);

    function abortHandler() {
      window.clearTimeout(timeoutId);
      signal.removeEventListener('abort', abortHandler);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    }

    if (signal) {
      signal.addEventListener('abort', abortHandler, { once: true });
    }
  });
}

function throwIfSignalAborted(signal) {
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
}

function getResponseHeader(response, headerName) {
  const headers = response?.headers;

  if (typeof headers?.get === 'function') {
    return headers.get(headerName);
  }

  return headers?.[headerName] ?? headers?.[headerName.toLowerCase()];
}

function parseRetryAfterSeconds(error) {
  const retryAfterHeader = getResponseHeader(error?.response, 'retry-after');
  const rawRetryAfterSeconds = retryAfterHeader
    ?? error?.response?.data?.retryAfterSeconds;
  const retryAfterSeconds = typeof rawRetryAfterSeconds === 'number'
    ? rawRetryAfterSeconds
    : Number.parseFloat(rawRetryAfterSeconds);

  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0) {
    return retryAfterSeconds;
  }

  return DEFAULT_GLB_CONVERTER_CAPACITY_RETRY_AFTER_SECONDS;
}

function getWorkspaceCapacityErrorDetails(error) {
  if (
    error?.response?.status !== 503
    || error?.response?.data?.code !== 'workspace_capacity'
  ) {
    return undefined;
  }

  return {
    reason: normalizeText(error.response.data.reason),
    responseStatusCode: error.response.status,
    retryAfterSeconds: parseRetryAfterSeconds(error),
  };
}

function getConverterBaseUrl() {
  const converterUrl = globalConfig.config.common.glbConverter;

  if (typeof converterUrl !== 'string' || converterUrl.trim().length === 0) {
    throw new ImportModelsConverterError('importModels.errors.converterConfigMissing');
  }

  return trimTrailingSlash(converterUrl.trim());
}

function getApiBaseUrl() {
  return trimTrailingSlash(globalConfig.config.common.api);
}

function normalizeInteger(value) {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsedValue = Number.parseInt(value, 10);

    if (Number.isInteger(parsedValue) && parsedValue > 0) {
      return parsedValue;
    }
  }

  return undefined;
}

function normalizeText(value) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();

  if (normalizedValue.length === 0) {
    return undefined;
  }

  return normalizedValue;
}

export function createGlbPendingRunStorageKey({
  projectGuid,
  sourceCalculationGuid,
} = {}) {
  const projectPart = normalizeText(projectGuid) ?? 'new-project';
  const sourcePart = normalizeText(sourceCalculationGuid) ?? 'uploaded-glb';

  return (
    `${GLB_PENDING_RUN_STORAGE_PREFIX}:` +
    `${encodeURIComponent(projectPart)}:${encodeURIComponent(sourcePart)}`
  );
}

function normalizeGlbPendingRun(record) {
  const jobId = normalizeText(record?.jobId);
  const phase = normalizeText(record?.phase);

  if (
    jobId === undefined ||
    !Object.values(GLB_PENDING_RUN_PHASES).includes(phase)
  ) {
    return null;
  }

  return {
    ...record,
    jobId,
    phase,
    version: 1,
  };
}

export function readGlbPendingRun({
  projectGuid,
  sourceCalculationGuid,
  storage = typeof window === 'undefined' ? undefined : window.sessionStorage,
} = {}) {
  if (!storage?.getItem) {
    return null;
  }

  try {
    const rawValue = storage.getItem(
      createGlbPendingRunStorageKey({
        projectGuid,
        sourceCalculationGuid,
      }),
    );

    return rawValue ? normalizeGlbPendingRun(JSON.parse(rawValue)) : null;
  } catch (_error) {
    return null;
  }
}

export function writeGlbPendingRun({
  projectGuid,
  record,
  sourceCalculationGuid,
  storage = typeof window === 'undefined' ? undefined : window.sessionStorage,
} = {}) {
  const normalizedRecord = normalizeGlbPendingRun(record);

  if (!normalizedRecord || !storage?.setItem) {
    return null;
  }

  try {
    storage.setItem(
      createGlbPendingRunStorageKey({
        projectGuid,
        sourceCalculationGuid,
      }),
      JSON.stringify(normalizedRecord),
    );
  } catch (_error) {
    // React state still preserves ownership while this component is mounted.
  }

  return normalizedRecord;
}

export function clearGlbPendingRun({
  projectGuid,
  sourceCalculationGuid,
  storage = typeof window === 'undefined' ? undefined : window.sessionStorage,
} = {}) {
  if (!storage?.removeItem) {
    return;
  }

  try {
    storage.removeItem(
      createGlbPendingRunStorageKey({
        projectGuid,
        sourceCalculationGuid,
      }),
    );
  } catch (_error) {
    // A storage failure must not hide the confirmed server outcome in the UI.
  }
}

function extractResponseText(value) {
  const normalizedValue = normalizeText(value);

  if (normalizedValue !== undefined) {
    return normalizedValue;
  }

  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return (
    normalizeText(value.data) ??
    normalizeText(value.tVal) ??
    normalizeText(value.TVal) ??
    normalizeText(value.value)
  );
}

function isNumericScaleText(value) {
  return /^(?:\d+(?:\.\d+)?|\.\d+)(?:e-?\d+)?$/iu.test(value);
}

function normalizeFiniteNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsedValue = Number.parseFloat(value.trim());

    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return undefined;
}

function formatMillimeterValue(value) {
  const normalizedValue = normalizeFiniteNumber(value);

  if (normalizedValue === undefined) {
    return undefined;
  }

  return (Math.round(normalizedValue * 100) / 100).toString();
}

function convertBoundsMetersToMillimeters(bounds) {
  const normalizedBounds = normalizeReportVector3(bounds);

  if (normalizedBounds === undefined) {
    return undefined;
  }

  return {
    x: normalizedBounds.x * 1000,
    y: normalizedBounds.y * 1000,
    z: normalizedBounds.z * 1000,
  };
}

function createModelScaleValidationError(modelScale) {
  return new ImportModelsConverterError(
    'importModels.errors.converterModelScaleInvalid',
    {
      modelScale: modelScale ?? '',
    },
  );
}

function createModelScaleModeValidationError(scaleMode) {
  return new ImportModelsConverterError(
    'importModels.errors.converterScaleModeInvalid',
    {
      scaleMode: scaleMode ?? '',
    },
  );
}

function createModelTargetBoundsValidationError(modelTargetBounds) {
  return new ImportModelsConverterError(
    'importModels.errors.converterModelTargetBoundsInvalid',
    {
      modelTargetBounds: modelTargetBounds ?? '',
    },
  );
}

function createRenderOutputModeValidationError(renderOutputMode) {
  return new ImportModelsConverterError(
    'importModels.errors.converterRenderOutputModeInvalid',
    {
      renderOutputMode: renderOutputMode ?? '',
    },
  );
}

export function buildConverterScreenshotOptions({
  renderOutputMode = DEFAULT_GLB_CONVERTER_RENDER_OUTPUT_MODE,
} = {}) {
  const normalizedRenderOutputMode = normalizeText(renderOutputMode);

  if (!GLB_CONVERTER_RENDER_OUTPUT_MODES.includes(normalizedRenderOutputMode)) {
    throw createRenderOutputModeValidationError(renderOutputMode);
  }

  return {
    mainIcon: true,
    sequence: normalizedRenderOutputMode === 'sequence',
    sphere: normalizedRenderOutputMode === 'sphere',
  };
}

function extractLegacyCalculationGuidFromResponseBody(legacyResponseBody) {
  const directGuid = normalizeText(legacyResponseBody?.guid);

  if (directGuid !== undefined) {
    return directGuid;
  }

  return normalizeText(legacyResponseBody?.TVal?.Guid);
}

function getResponseBody(responseData) {
  if (responseData !== null && typeof responseData === 'object' && !Array.isArray(responseData)) {
    const wrappedData = responseData.data;

    if (wrappedData !== null && typeof wrappedData === 'object') {
      return wrappedData;
    }
  }

  return responseData;
}

function getApiResultPayload(responseData) {
  const responseBody = getResponseBody(responseData);

  if (responseBody !== null && typeof responseBody === 'object' && !Array.isArray(responseBody)) {
    const tValPayload = responseBody.tVal ?? responseBody.TVal;

    if (tValPayload !== null && typeof tValPayload === 'object') {
      return tValPayload;
    }
  }

  return responseBody;
}

function readObjectField(value, camelCaseName, pascalCaseName) {
  if (value === null || typeof value !== 'object') {
    return undefined;
  }

  return value[camelCaseName] ?? value[pascalCaseName];
}

function isEnabledByDefault(value) {
  return value !== false;
}

function normalizeStoredCalculationFile(calculationFile, targetFileName) {
  const file = readObjectField(calculationFile, 'file', 'File') ?? calculationFile;
  const fileName =
    normalizeText(readObjectField(file, 'name', 'Name')) ??
    normalizeText(readObjectField(calculationFile, 'name', 'Name'));
  const filePath =
    normalizeText(readObjectField(file, 'path', 'Path')) ??
    normalizeText(readObjectField(calculationFile, 'path', 'Path'));
  const relationEnabled = isEnabledByDefault(
    readObjectField(calculationFile, 'isEnabled', 'IsEnabled'),
  );
  const fileEnabled = isEnabledByDefault(readObjectField(file, 'isEnabled', 'IsEnabled'));

  if (
    fileName !== targetFileName ||
    filePath === undefined ||
    !relationEnabled ||
    !fileEnabled
  ) {
    return undefined;
  }

  return {
    name: fileName,
    path: filePath,
  };
}

export function extractStoredCalculationFile(calculationStatResponseData, targetFileName) {
  const calculationStat = getApiResultPayload(calculationStatResponseData);
  const calculationFiles = readObjectField(
    calculationStat,
    'calculationFiles',
    'CalculationFiles',
  );

  if (!Array.isArray(calculationFiles)) {
    return undefined;
  }

  return calculationFiles
    .map((calculationFile) => normalizeStoredCalculationFile(calculationFile, targetFileName))
    .find((sourceFile) => sourceFile !== undefined);
}

export function extractStoredGlbSourceFile(calculationStatResponseData) {
  return extractStoredCalculationFile(
    calculationStatResponseData,
    GLB_CONVERTER_SOURCE_FILE_NAME,
  );
}

export function extractStoredModelTransformReportFile(calculationStatResponseData) {
  return extractStoredCalculationFile(
    calculationStatResponseData,
    GLB_CONVERTER_MODEL_TRANSFORM_REPORT_FILE_NAME,
  );
}

function extractProjectCalculationGuid(calculations) {
  if (!Array.isArray(calculations)) {
    return undefined;
  }

  const mainVariantGuid = calculations
    .find((calculation) => calculation?.isMainVariant === true)
    ?.calculationGuid;

  const normalizedMainVariantGuid = normalizeText(mainVariantGuid);

  if (normalizedMainVariantGuid !== undefined) {
    return normalizedMainVariantGuid;
  }

  const calculationGuids = calculations
    .map((calculation) => normalizeText(calculation?.calculationGuid))
    .filter((calculationGuid) => calculationGuid !== undefined);

  return calculationGuids.at(-1);
}

function isAbortError(error) {
  return axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'AbortError';
}

function sanitizeDebugFilePart(value, fallbackValue) {
  const normalizedValue = normalizeText(value);

  if (normalizedValue === undefined) {
    return fallbackValue;
  }

  const sanitizedValue = normalizedValue
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (sanitizedValue.length === 0) {
    return fallbackValue;
  }

  return sanitizedValue;
}

export function resolveConverterModelScale({
  customModelScale = '',
  modelScale = DEFAULT_GLB_CONVERTER_MODEL_SCALE,
} = {}) {
  const isCustomScale = modelScale === CUSTOM_GLB_CONVERTER_MODEL_SCALE;
  const rawModelScale = isCustomScale ? customModelScale : modelScale;
  const normalizedModelScale = normalizeText(rawModelScale);

  if (!isCustomScale && normalizedModelScale === undefined) {
    return DEFAULT_GLB_CONVERTER_MODEL_SCALE;
  }

  if (normalizedModelScale === undefined) {
    throw createModelScaleValidationError(rawModelScale);
  }

  if (normalizedModelScale === DEFAULT_GLB_CONVERTER_MODEL_SCALE) {
    return DEFAULT_GLB_CONVERTER_MODEL_SCALE;
  }

  const parsedModelScale = Number.parseFloat(normalizedModelScale);

  if (
    !isNumericScaleText(normalizedModelScale) ||
    !Number.isFinite(parsedModelScale) ||
    parsedModelScale <= 0
  ) {
    throw createModelScaleValidationError(rawModelScale);
  }

  return parsedModelScale.toString();
}

function readTargetBoundsAxis(targetBoundsMillimeters, axisName) {
  if (targetBoundsMillimeters === null || typeof targetBoundsMillimeters !== 'object') {
    return undefined;
  }

  return normalizeText(targetBoundsMillimeters[axisName]);
}

export function resolveConverterModelTargetBounds({
  targetBoundsMillimeters = {},
} = {}) {
  const rawBounds = {
    x: readTargetBoundsAxis(targetBoundsMillimeters, 'x'),
    y: readTargetBoundsAxis(targetBoundsMillimeters, 'y'),
    z: readTargetBoundsAxis(targetBoundsMillimeters, 'z'),
  };
  const parsedBounds = {
    x: Number.parseFloat(rawBounds.x ?? ''),
    y: Number.parseFloat(rawBounds.y ?? ''),
    z: Number.parseFloat(rawBounds.z ?? ''),
  };

  if (
    rawBounds.x === undefined ||
    rawBounds.y === undefined ||
    rawBounds.z === undefined ||
    !isNumericScaleText(rawBounds.x) ||
    !isNumericScaleText(rawBounds.y) ||
    !isNumericScaleText(rawBounds.z) ||
    !Number.isFinite(parsedBounds.x) ||
    !Number.isFinite(parsedBounds.y) ||
    !Number.isFinite(parsedBounds.z) ||
    parsedBounds.x <= 0 ||
    parsedBounds.y <= 0 ||
    parsedBounds.z <= 0
  ) {
    throw createModelTargetBoundsValidationError(targetBoundsMillimeters);
  }

  return {
    x: parsedBounds.x / 1000,
    y: parsedBounds.y / 1000,
    z: parsedBounds.z / 1000,
  };
}

export function resolveConverterScaleRequest({
  customModelScale = '',
  modelScale = DEFAULT_GLB_CONVERTER_COEFFICIENT_SCALE,
  scaleMode = DEFAULT_GLB_CONVERTER_SCALE_MODE,
  targetBoundsMillimeters = {},
} = {}) {
  const normalizedScaleMode = normalizeText(scaleMode) ?? DEFAULT_GLB_CONVERTER_SCALE_MODE;

  if (!GLB_CONVERTER_SCALE_MODES.includes(normalizedScaleMode)) {
    throw createModelScaleModeValidationError(scaleMode);
  }

  if (normalizedScaleMode === 'auto') {
    return {
      modelScale: DEFAULT_GLB_CONVERTER_MODEL_SCALE,
      modelTargetBounds: undefined,
      scaleMode: normalizedScaleMode,
    };
  }

  if (normalizedScaleMode === 'coefficient') {
    return {
      modelScale: resolveConverterModelScale({
        customModelScale,
        modelScale,
      }),
      modelTargetBounds: undefined,
      scaleMode: normalizedScaleMode,
    };
  }

  return {
    modelScale: undefined,
    modelTargetBounds: resolveConverterModelTargetBounds({
      targetBoundsMillimeters,
    }),
    scaleMode: normalizedScaleMode,
  };
}

function buildEmptyTargetBoundsMillimeters() {
  return {
    x: '',
    y: '',
    z: '',
  };
}

function buildTargetBoundsMillimeterText(bounds) {
  const boundsMillimeters = convertBoundsMetersToMillimeters(bounds);

  if (boundsMillimeters === undefined) {
    return buildEmptyTargetBoundsMillimeters();
  }

  return {
    x: formatMillimeterValue(boundsMillimeters.x) ?? '',
    y: formatMillimeterValue(boundsMillimeters.y) ?? '',
    z: formatMillimeterValue(boundsMillimeters.z) ?? '',
  };
}

function buildCoefficientScaleSettings(scaleValue) {
  const normalizedScaleValue = normalizeFiniteNumber(scaleValue);

  if (normalizedScaleValue === undefined || normalizedScaleValue <= 0) {
    return {
      customModelScale: '',
      modelScale: DEFAULT_GLB_CONVERTER_COEFFICIENT_SCALE,
    };
  }

  const modelScale = normalizedScaleValue.toString();

  if (GLB_CONVERTER_MODEL_SCALE_PRESETS.includes(modelScale)) {
    return {
      customModelScale: '',
      modelScale,
    };
  }

  return {
    customModelScale: modelScale,
    modelScale: CUSTOM_GLB_CONVERTER_MODEL_SCALE,
  };
}

function resolveRetryCoefficientScale({ modelTransformReport, requestedScale, reportScaleMode }) {
  if (reportScaleMode === 'manual' && requestedScale !== undefined) {
    const parsedModelScale = Number.parseFloat(requestedScale);

    if (
      isNumericScaleText(requestedScale) &&
      Number.isFinite(parsedModelScale) &&
      parsedModelScale > 0
    ) {
      return parsedModelScale;
    }
  }

  return normalizeFiniteNumber(modelTransformReport?.resolvedScale);
}

export function buildConverterRetryScaleSettings(modelTransformReport) {
  const targetBoundsMillimeters = buildTargetBoundsMillimeterText(
    modelTransformReport?.outputBounds?.size,
  );
  const reportScaleMode = normalizeText(modelTransformReport?.scaleMode);
  const requestedScale = normalizeText(modelTransformReport?.requestedScale);
  const coefficientScaleSettings = buildCoefficientScaleSettings(
    resolveRetryCoefficientScale({
      modelTransformReport,
      requestedScale,
      reportScaleMode,
    }),
  );

  if (reportScaleMode === 'bounds') {
    return {
      customModelScale: coefficientScaleSettings.customModelScale,
      modelScale: coefficientScaleSettings.modelScale,
      scaleMode: 'bounds',
      targetBoundsMillimeters,
    };
  }

  if (reportScaleMode === 'manual' && requestedScale !== undefined) {
    return {
      customModelScale: coefficientScaleSettings.customModelScale,
      modelScale: coefficientScaleSettings.modelScale,
      scaleMode: 'coefficient',
      targetBoundsMillimeters,
    };
  }

  return {
    customModelScale: coefficientScaleSettings.customModelScale,
    modelScale: coefficientScaleSettings.modelScale,
    scaleMode: DEFAULT_GLB_CONVERTER_SCALE_MODE,
    targetBoundsMillimeters,
  };
}

export function getImportModelsSelectionState(selectedFiles) {
  if (!Array.isArray(selectedFiles) || selectedFiles.length === 0) {
    return {
      errorMessageKey: undefined,
      mode: 'legacy',
    };
  }

  const hasGlbFile = selectedFiles.some(
    (file) => getNormalizedFileExtension(file?.name) === '.glb',
  );

  if (!hasGlbFile) {
    return {
      errorMessageKey: undefined,
      mode: 'legacy',
    };
  }

  if (selectedFiles.length === 1 && getNormalizedFileExtension(selectedFiles[0]?.name) === '.glb') {
    const fileLimitErrorDetails = getGlbUploadLimitErrorDetails(selectedFiles[0]);

    if (fileLimitErrorDetails !== undefined) {
      return {
        errorMessageKey: 'importModels.errors.glbTooLarge',
        errorMessageValues: fileLimitErrorDetails,
        mode: 'invalid',
      };
    }

    return {
      errorMessageKey: undefined,
      mode: 'converter',
    };
  }

  return {
    errorMessageKey: 'importModels.errors.singleGlbOnly',
    mode: 'invalid',
  };
}

export function getDefaultModelName(selectedFiles) {
  if (!Array.isArray(selectedFiles) || selectedFiles.length === 0) {
    return '';
  }

  const preferredFile =
    selectedFiles.find((file) => {
      const normalizedExtension = getNormalizedFileExtension(file?.name);

      return normalizedExtension === '.glb' || normalizedExtension === '.3ds' || normalizedExtension === '.blend';
    }) ?? selectedFiles[0];

  const fileName = typeof preferredFile?.name === 'string' ? preferredFile.name : '';

  return fileName.replace(/\.[^/.]+$/, '');
}

export function buildOidcStorageKey({ authority, clientId }) {
  return `oidc.user:${authority}:${clientId}`;
}

export function parseAccessTokenFromStorageValue(rawValue) {
  if (typeof rawValue !== 'string' || rawValue.trim().length === 0) {
    throw new ImportModelsConverterError('importModels.errors.converterTokenMissing');
  }

  let parsedValue;

  try {
    parsedValue = JSON.parse(rawValue);
  } catch (error) {
    throw new ImportModelsConverterError('importModels.errors.converterTokenInvalid');
  }

  const accessToken = normalizeText(parsedValue?.access_token);

  if (accessToken === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterTokenInvalid');
  }

  return accessToken;
}

export function readAccessTokenFromStorage(storage = window.localStorage) {
  const storageKey = buildOidcStorageKey({
    authority: globalConfig.config.auth.authority,
    clientId: globalConfig.config.auth.client_id,
  });

  return parseAccessTokenFromStorageValue(storage.getItem(storageKey));
}

export function parseCompanyIdFromStorageValue(rawValue) {
  if (typeof rawValue !== 'string' || rawValue.trim().length === 0) {
    throw new ImportModelsConverterError('importModels.errors.converterCompanyMissing');
  }

  let parsedValue;

  try {
    parsedValue = JSON.parse(rawValue);
  } catch (error) {
    throw new ImportModelsConverterError('importModels.errors.converterCompanyMissing');
  }

  const companyId = normalizeInteger(parsedValue?.id);

  if (companyId === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterCompanyMissing');
  }

  return companyId;
}

export function readCompanyIdFromStorage(storage = window.localStorage) {
  return parseCompanyIdFromStorageValue(storage.getItem('company'));
}

export function activateConverterTargetCompany({
  storage = window.localStorage,
  targetCompanyId,
} = {}) {
  if (targetCompanyId === undefined || targetCompanyId === null) {
    return readCompanyIdFromStorage(storage);
  }

  const companyId = normalizeInteger(targetCompanyId);

  if (companyId === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterCompanyMissing');
  }

  storage.setItem('company', JSON.stringify({ id: companyId }));

  return companyId;
}

export function buildGlbRetryTargetState(actionContext) {
  if (actionContext?.accessAllowed !== true) {
    return {
      retryTargetCompanyId: null,
      retryTargetProjectGuid: null,
    };
  }

  return {
    retryTargetCompanyId: actionContext.targetCompanyId ?? null,
    retryTargetProjectGuid: actionContext.targetProjectGuid ?? null,
  };
}

export async function resolveGlbRetryActionContext({
  currentProjectGuid,
  signal,
  sourceProjectGuid,
}) {
  const normalizedCurrentProjectGuid = normalizeText(currentProjectGuid);

  if (normalizedCurrentProjectGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterProjectResolveFailed');
  }

  const normalizedSourceProjectGuid = normalizeText(sourceProjectGuid);

  if (normalizedSourceProjectGuid === undefined) {
    return {
      accessAllowed: true,
      companyName: '',
      targetCompanyId: undefined,
      targetProjectGuid: normalizedCurrentProjectGuid,
    };
  }

  const [sourceCompanyResponse, userCompaniesResponse] = await Promise.all([
    axios.get(`${getApiBaseUrl()}/Companies/GetCompanyByProjectGuid`, {
      params: {
        projectGuid: normalizedSourceProjectGuid,
      },
      signal,
      withCredentials: true,
    }),
    axios.get(`${getApiBaseUrl()}/Companies/GetForUser`, {
      signal,
      withCredentials: true,
    }),
  ]);
  const sourceCompany = getResponseBody(sourceCompanyResponse.data);
  const userCompanies = getResponseBody(userCompaniesResponse.data);
  const sourceCompanyId = normalizeInteger(sourceCompany?.id);
  const accessAllowed =
    sourceCompanyId !== undefined &&
    Array.isArray(userCompanies) &&
    userCompanies.some((company) => normalizeInteger(company?.id) === sourceCompanyId);

  return {
    accessAllowed,
    companyName: normalizeText(sourceCompany?.name) ?? '',
    targetCompanyId: accessAllowed ? sourceCompanyId : undefined,
    targetProjectGuid: accessAllowed ? normalizedSourceProjectGuid : undefined,
  };
}

export function extractDisplayTapeGuid(responseData) {
  const responseBody = getResponseBody(responseData);
  const displayTapeGuid = normalizeText(responseBody?.guidDisplayTape);

  if (displayTapeGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterDisplayTapeCreateFailed');
  }

  return displayTapeGuid;
}

export function buildCreateProjectFromDisplayTapesPayload({
  displayTapeGuid,
  idCompany,
  modelName,
}) {
  return {
    displayTapeGuids: [displayTapeGuid],
    project: {
      idCompany,
      isImport: true,
      name: modelName,
      sourceOfCreation: '.glb',
    },
  };
}

export function extractProjectRedirectTarget(responseData) {
  const responseBody = getResponseBody(responseData);
  const projectGuid = normalizeText(responseBody?.projectGuid);
  const variantGuid =
    normalizeText(responseBody?.guidMainCalculation) ??
    extractProjectCalculationGuid(responseBody?.calculations);

  if (projectGuid === undefined || variantGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterChatProjectCreateFailed');
  }

  return {
    projectGuid,
    variantGuid,
  };
}

export function resolveConverterRedirectTarget({
  calculationGuid,
  createdProjectResponseData,
  existingProjectGuid,
}) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);

  if (normalizedCalculationGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
  }

  const normalizedExistingProjectGuid = normalizeText(existingProjectGuid);

  if (normalizedExistingProjectGuid !== undefined) {
    return {
      flow: 'retry_variant',
      projectGuid: normalizedExistingProjectGuid,
      variantGuid: normalizedCalculationGuid,
    };
  }

  return {
    flow: 'new_project',
    ...extractProjectRedirectTarget(createdProjectResponseData),
  };
}

export function resolveGlbConversionAuditCalculationGuid({
  calculationGuid,
  redirectTarget,
}) {
  return redirectTarget.flow === 'new_project'
    ? redirectTarget.variantGuid
    : calculationGuid;
}

export function extractLegacyCalculationGuid(jobStatus) {
  const publishStatus = normalizeText(jobStatus?.publish?.status);

  if (jobStatus?.status !== 'done' || publishStatus !== 'succeeded') {
    throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
  }

  const calculationGuid =
    normalizeText(jobStatus?.publish?.legacyCalculationGuid) ??
    extractLegacyCalculationGuidFromResponseBody(jobStatus?.publish?.legacyResponseBody);

  if (calculationGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
  }

  return calculationGuid;
}

export function buildConverterDebugEntry({ capturedAt = new Date().toISOString(), event, payload }) {
  return {
    capturedAt,
    event,
    payload,
  };
}

export function buildConverterDebugErrorPayload(error) {
  if (error instanceof ImportModelsConverterError) {
    return {
      details: error.details,
      message: error.message,
      messageKey: error.messageKey,
      name: error.name,
    };
  }

  return {
    code: normalizeText(error?.code),
    message: normalizeText(error?.message) ?? String(error),
    name: normalizeText(error?.name),
    responseData: error?.response?.data,
    responseStatusCode: error?.response?.status,
  };
}

export function buildGlbConversionRetryActionMessagePayload({
  calculationGuid,
  projectGuid,
}) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);
  const normalizedProjectGuid = normalizeText(projectGuid);

  if (normalizedCalculationGuid === undefined || normalizedProjectGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
  }

  return {
    ActionType: GLB_RETRY_ACTION_TYPE,
    Content: GLB_RETRY_ACTION_MESSAGE_CONTENT,
    FormatType: 'Action',
    GuidCalculation: normalizedCalculationGuid,
    GuidProject: normalizedProjectGuid,
    Type: 'System',
  };
}

export function buildGlbSourceLinkMessagePayload({
  calculationGuid,
  projectGuid,
  sourceFileUrl,
}) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);
  const normalizedProjectGuid = normalizeText(projectGuid);
  const normalizedSourceFileUrl = normalizeText(sourceFileUrl);

  if (
    normalizedCalculationGuid === undefined ||
    normalizedProjectGuid === undefined ||
    normalizedSourceFileUrl === undefined
  ) {
    throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
  }

  return {
    Content: `${GLB_SOURCE_LINK_MESSAGE_PREFIX} ${normalizedSourceFileUrl}`,
    FormatType: 'Text',
    GuidCalculation: normalizedCalculationGuid,
    GuidProject: normalizedProjectGuid,
    Type: 'System',
  };
}

export function buildGlbSourceLinkFallbackMessagePayload({
  calculationGuid,
  projectGuid,
}) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);
  const normalizedProjectGuid = normalizeText(projectGuid);

  if (normalizedCalculationGuid === undefined || normalizedProjectGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
  }

  return {
    Content: GLB_SOURCE_LINK_FALLBACK_MESSAGE_CONTENT,
    FormatType: 'Text',
    GuidCalculation: normalizedCalculationGuid,
    GuidProject: normalizedProjectGuid,
    Type: 'System',
  };
}

export function buildConverterDebugReport({
  context,
  entries,
  generatedAt = new Date().toISOString(),
}) {
  return JSON.stringify(
    {
      context,
      entries,
      generatedAt,
    },
    null,
    2,
  );
}

export function createConverterDebugFileName({
  createdAt = new Date().toISOString(),
  jobId,
  modelName,
}) {
  const timestamp = createdAt.replace(/[:.]/g, '-');
  const sanitizedModelName = sanitizeDebugFilePart(modelName, 'model');
  const sanitizedJobId = sanitizeDebugFilePart(jobId, 'pending-job');

  return `glb-import-debug-${sanitizedModelName}-${sanitizedJobId}-${timestamp}.json`;
}

export function buildCreateJobFormData({
  file,
  idCompany,
  modelName,
  modelScale = DEFAULT_GLB_CONVERTER_MODEL_SCALE,
  modelTargetBounds,
  optimizationSettings = DEFAULT_GLB_TRANSFORMATION_SETTINGS,
  projectGuid,
  renderOutputMode = DEFAULT_GLB_CONVERTER_RENDER_OUTPUT_MODE,
}) {
  const normalizedProjectGuid = normalizeText(projectGuid);
  const resolvedModelScale = modelTargetBounds === undefined
    ? resolveConverterModelScale({
        modelScale,
      })
    : undefined;
  const screenshotOptions = buildConverterScreenshotOptions({
    renderOutputMode,
  });
  const requestPayload = {
    idCompany,
    name: modelName,
  };

  if (normalizedProjectGuid !== undefined) {
    requestPayload.projectGuid = normalizedProjectGuid;
  }

  const formData = new FormData();
  formData.append('modelFile', file);
  if (resolvedModelScale !== undefined) {
    formData.append('modelScale', resolvedModelScale);
  }
  if (modelTargetBounds !== undefined) {
    formData.append('modelTargetBounds', JSON.stringify(modelTargetBounds));
  }
  formData.append(
    'optimizationSettings',
    JSON.stringify(optimizationSettings),
  );
  formData.append('request', JSON.stringify(requestPayload));
  formData.append('screenshotOptions', JSON.stringify(screenshotOptions));

  return formData;
}

export async function createGlbConversionJob({
  accessToken,
  file,
  idCompany,
  modelName,
  modelScale = DEFAULT_GLB_CONVERTER_MODEL_SCALE,
  modelTargetBounds,
  onUploadProgress,
  optimizationSettings,
  projectGuid,
  renderOutputMode = DEFAULT_GLB_CONVERTER_RENDER_OUTPUT_MODE,
  signal,
}) {
  const fileLimitErrorDetails = getGlbUploadLimitErrorDetails(file);

  if (fileLimitErrorDetails !== undefined) {
    throw new ImportModelsConverterError(
      'importModels.errors.glbTooLarge',
      fileLimitErrorDetails,
    );
  }

  const converterBaseUrl = getConverterBaseUrl();
  let response;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      response = await axios.post(
        `${converterBaseUrl}/jobs`,
        buildCreateJobFormData({
          file,
          idCompany,
          modelName,
          modelScale,
          modelTargetBounds,
          optimizationSettings,
          projectGuid,
          renderOutputMode,
        }),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (typeof onUploadProgress !== 'function' || progressEvent.total === undefined) {
              return;
            }

            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onUploadProgress(percent);
          },
          signal,
        },
      );
      break;
    } catch (error) {
      const capacityErrorDetails = getWorkspaceCapacityErrorDetails(error);

      if (capacityErrorDetails === undefined) {
        throw error;
      }

      if (attempt === 0) {
        await delay(capacityErrorDetails.retryAfterSeconds * 1000, signal);
        continue;
      }

      throw new ImportModelsConverterError(
        'importModels.errors.converterCapacityUnavailable',
        capacityErrorDetails,
      );
    }
  }

  const jobId = normalizeText(response.data?.jobId);

  if (jobId === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
  }

  return {
    jobId,
    pollUrl: normalizeText(response.data?.pollUrl),
    status: normalizeText(response.data?.status),
  };
}

async function getStoredGlbSourceFile({ calculationGuid, signal }) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);

  if (normalizedCalculationGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterSourceMissing');
  }

  try {
    const calculationStatResponse = await axios.get(
      `${getApiBaseUrl()}/Calculation/GetCalculationStat`,
      {
        params: {
          guid: normalizedCalculationGuid,
        },
        signal,
        withCredentials: true,
      },
    );
    const storedSourceFile = extractStoredGlbSourceFile(calculationStatResponse.data);

    if (storedSourceFile === undefined) {
      throw new ImportModelsConverterError('importModels.errors.converterSourceMissing', {
        sourceFileName: GLB_CONVERTER_SOURCE_FILE_NAME,
      });
    }

    return storedSourceFile;
  } catch (error) {
    if (error instanceof ImportModelsConverterError) {
      throw error;
    }

    throw normalizeConverterOperationError(
      'importModels.errors.converterSourceDownloadFailed',
      error,
    );
  }
}

export async function getStoredGlbSourceFileUrl({ calculationGuid, signal }) {
  const storedSourceFile = await getStoredGlbSourceFile({
    calculationGuid,
    signal,
  });

  try {
    const sourceFileUrlResponse = await axios.get(`${getApiBaseUrl()}/Data/GetFileUrlInCloud`, {
      params: {
        pathToFile: storedSourceFile.path,
      },
      signal,
      withCredentials: true,
    });
    const sourceFileUrl = extractResponseText(sourceFileUrlResponse.data);

    if (sourceFileUrl === undefined) {
      throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
    }

    return {
      ...storedSourceFile,
      url: sourceFileUrl,
    };
  } catch (error) {
    if (error instanceof ImportModelsConverterError) {
      throw error;
    }

    throw normalizeConverterOperationError(
      'importModels.errors.converterSourceDownloadFailed',
      error,
    );
  }
}

export async function downloadStoredGlbSourceFile({ calculationGuid, signal }) {
  const storedSourceFile = await getStoredGlbSourceFile({
    calculationGuid,
    signal,
  });

  try {
    const sourceFileResponse = await axios.get(`${getApiBaseUrl()}/Data/DownloadFile`, {
      params: {
        pathToFile: storedSourceFile.path,
      },
      responseType: 'blob',
      signal,
      withCredentials: true,
    });
    const sourceBlob = typeof Blob !== 'undefined' && sourceFileResponse.data instanceof Blob
      ? sourceFileResponse.data
      : new Blob([sourceFileResponse.data], {
        type: GLB_CONVERTER_SOURCE_CONTENT_TYPE,
      });

    return new File([sourceBlob], GLB_CONVERTER_SOURCE_FILE_NAME, {
      type: GLB_CONVERTER_SOURCE_CONTENT_TYPE,
    });
  } catch (error) {
    if (error instanceof ImportModelsConverterError) {
      throw error;
    }

    throw normalizeConverterOperationError(
      'importModels.errors.converterSourceDownloadFailed',
      error,
    );
  }
}

function normalizeReportVector3(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  if (
    typeof value.x !== 'number' ||
    typeof value.y !== 'number' ||
    typeof value.z !== 'number' ||
    !Number.isFinite(value.x) ||
    !Number.isFinite(value.y) ||
    !Number.isFinite(value.z)
  ) {
    return undefined;
  }

  return {
    x: value.x,
    y: value.y,
    z: value.z,
  };
}

function normalizeModelTransformReport(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const outputBounds = value.outputBounds;
  const outputBoundsSize = normalizeReportVector3(outputBounds?.size);
  const scaleMode = normalizeText(value.scaleMode);
  const requestedScale = normalizeText(value.requestedScale);

  if (
    outputBoundsSize === undefined ||
    !['auto', 'bounds', 'manual'].includes(scaleMode) ||
    requestedScale === undefined
  ) {
    return undefined;
  }

  return {
    ...value,
    outputBounds: {
      ...outputBounds,
      size: outputBoundsSize,
    },
    requestedBounds: normalizeReportVector3(value.requestedBounds) ?? null,
    requestedScale,
    scaleMode,
  };
}

async function parseDownloadedJsonResponseData(responseData, signal) {
  throwIfSignalAborted(signal);

  if (typeof Blob !== 'undefined' && responseData instanceof Blob) {
    const responseText = await responseData.text();

    throwIfSignalAborted(signal);
    return JSON.parse(responseText);
  }

  if (typeof responseData === 'string') {
    return JSON.parse(responseData);
  }

  return responseData;
}

export async function downloadStoredModelTransformReport({ calculationGuid, signal }) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);

  if (normalizedCalculationGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterSourceMissing');
  }

  try {
    const calculationStatResponse = await axios.get(
      `${getApiBaseUrl()}/Calculation/GetCalculationStat`,
      {
        params: {
          guid: normalizedCalculationGuid,
        },
        signal,
        withCredentials: true,
      },
    );
    const storedReportFile = extractStoredModelTransformReportFile(calculationStatResponse.data);

    if (storedReportFile === undefined) {
      throw new ImportModelsConverterError('importModels.errors.converterTransformReportMissing', {
        sourceFileName: GLB_CONVERTER_MODEL_TRANSFORM_REPORT_FILE_NAME,
      });
    }

    const reportResponse = await axios.get(`${getApiBaseUrl()}/Data/DownloadFile`, {
      params: {
        pathToFile: storedReportFile.path,
      },
      signal,
      withCredentials: true,
    });
    const parsedReport = await parseDownloadedJsonResponseData(
      reportResponse.data,
      signal,
    );
    const normalizedReport = normalizeModelTransformReport(parsedReport);

    if (normalizedReport === undefined) {
      throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
    }

    return normalizedReport;
  } catch (error) {
    if (error instanceof ImportModelsConverterError) {
      throw error;
    }

    throw normalizeConverterOperationError(
      'importModels.errors.converterTransformReportDownloadFailed',
      error,
    );
  }
}

export async function getGlbConversionJob({ accessToken, jobId, signal }) {
  const converterBaseUrl = getConverterBaseUrl();
  const response = await axios.get(`${converterBaseUrl}/jobs/${jobId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
    timeout: 60000,
  });

  return response.data;
}

export async function pollGlbConversionJob({
  accessToken,
  getAccessToken,
  jobId,
  onRetryableError,
  onStatusChange,
  signal,
}) {
  let consecutiveFailures = 0;
  let lastStatusReceivedAt = Date.now();

  while (true) {
    try {
      const jobStatus = await getGlbConversionJob({
        accessToken: typeof getAccessToken === 'function'
          ? getAccessToken()
          : accessToken,
        jobId,
        signal,
      });

      if (!['queued', 'running', 'done', 'failed', 'publish_unknown'].includes(jobStatus?.status)) {
        throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
      }

      consecutiveFailures = 0;
      lastStatusReceivedAt = Date.now();

      if (typeof onStatusChange === 'function') {
        onStatusChange(jobStatus);
      }

      if (jobStatus?.status === 'failed') {
        throw new ImportModelsConverterError('importModels.errors.converterJobFailed', {
          errorMessage: normalizeText(jobStatus?.errorMessage),
        });
      }

      if (jobStatus?.status === 'publish_unknown') {
        throw new ImportModelsConverterError('importModels.errors.converterPublishUnknown', {
          errorMessage: normalizeText(jobStatus?.publish?.errorMessage ?? jobStatus?.errorMessage),
        });
      }

      if (jobStatus?.status === 'done') {
        if (jobStatus?.publish?.status !== 'succeeded') {
          throw new ImportModelsConverterError('importModels.errors.converterInvalidResponse');
        }

        return jobStatus;
      }

      await delay(CONVERTER_POLL_INTERVAL_MS, signal);
    } catch (error) {
      if (error instanceof ImportModelsConverterError || isAbortError(error)) {
        throw error;
      }

      const responseStatusCode = error?.response?.status;
      const isRetryableStatus = responseStatusCode === undefined
        || [408, 425, 429].includes(responseStatusCode)
        || (responseStatusCode >= 500 && responseStatusCode < 600);

      if (!isRetryableStatus) {
        throw normalizeConverterOperationError(
          [401, 403].includes(responseStatusCode)
            ? 'importModels.errors.converterPollingAccessDenied'
            : 'importModels.errors.converterPollingFailed',
          error,
        );
      }

      consecutiveFailures += 1;

      if (typeof onRetryableError === 'function') {
        onRetryableError({
          attempt: consecutiveFailures,
          elapsedSinceLastStatusMs: Date.now() - lastStatusReceivedAt,
          error,
        });
      }

      await delay(CONVERTER_POLL_INTERVAL_MS, signal);
    }
  }
}

function normalizeConverterOperationError(messageKey, error) {
  if (error instanceof ImportModelsConverterError || isAbortError(error)) {
    return error;
  }

  return new ImportModelsConverterError(messageKey, {
    errorMessage: normalizeText(error?.response?.data) ?? normalizeText(error?.message),
    responseData: error?.response?.data,
    responseStatusCode: error?.response?.status,
  });
}

export async function createDisplayTapeFromCalculation({
  calculationGuid,
  idCompany,
  signal,
}) {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/Projects/AddDisplayTape`,
      {
        guidCalculation: calculationGuid,
        idCompany,
      },
      {
        signal,
        withCredentials: true,
      },
    );

    return {
      displayTapeGuid: extractDisplayTapeGuid(response.data),
      responseData: response.data,
    };
  } catch (error) {
    throw normalizeConverterOperationError(
      'importModels.errors.converterDisplayTapeCreateFailed',
      error,
    );
  }
}

export async function createChatProjectFromDisplayTape({
  displayTapeGuid,
  idCompany,
  modelName,
  signal,
}) {
  const requestData = buildCreateProjectFromDisplayTapesPayload({
    displayTapeGuid,
    idCompany,
    modelName,
  });

  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/Projects/CreateProjectFromDisplayTapes`,
      requestData,
      {
        signal,
        withCredentials: true,
      },
    );
    const redirectTarget = extractProjectRedirectTarget(response.data);

    return {
      ...redirectTarget,
      requestData,
      responseData: response.data,
    };
  } catch (error) {
    throw normalizeConverterOperationError(
      'importModels.errors.converterChatProjectCreateFailed',
      error,
    );
  }
}

export async function sendGlbConversionRetryActionMessage({
  calculationGuid,
  projectGuid,
  signal,
}) {
  const response = await axios.post(
    `${getApiBaseUrl()}/ChatMessages/SendMessage`,
    buildGlbConversionRetryActionMessagePayload({
      calculationGuid,
      projectGuid,
    }),
    {
      signal,
      withCredentials: true,
    },
  );

  return response.data;
}

export async function sendGlbSourceLinkMessage({
  calculationGuid,
  projectGuid,
  signal,
}) {
  const sourceFile = await getStoredGlbSourceFileUrl({
    calculationGuid,
    signal,
  });
  const response = await axios.post(
    `${getApiBaseUrl()}/ChatMessages/SendMessage`,
    buildGlbSourceLinkMessagePayload({
      calculationGuid,
      projectGuid,
      sourceFileUrl: sourceFile.url,
    }),
    {
      signal,
      withCredentials: true,
    },
  );

  return {
    responseData: response.data,
    sourceFile,
  };
}

export async function sendGlbSourceLinkFallbackMessage({
  calculationGuid,
  projectGuid,
  signal,
}) {
  const response = await axios.post(
    `${getApiBaseUrl()}/ChatMessages/SendMessage`,
    buildGlbSourceLinkFallbackMessagePayload({
      calculationGuid,
      projectGuid,
    }),
    {
      signal,
      withCredentials: true,
    },
  );

  return response.data;
}

export async function resolveProjectGuidByCalculation({ calculationGuid, signal }) {
  const response = await axios.get(`${getApiBaseUrl()}/Projects/GetProjectInfoByCalculation`, {
    params: {
      guidCalculation: calculationGuid,
    },
    signal,
    withCredentials: true,
  });

  const projectGuid = normalizeText(response.data?.projectGuid ?? response.data?.data?.projectGuid);

  if (projectGuid === undefined) {
    throw new ImportModelsConverterError('importModels.errors.converterProjectResolveFailed');
  }

  return projectGuid;
}

export function buildConverterErrorMessage({ error, translate }) {
  if (!(error instanceof ImportModelsConverterError)) {
    return translate('importModels.messages.uploadError');
  }

  return translate(error.messageKey, error.details);
}
